import { useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import LookupChipCells from "./LookupChipCells";
import SelectionCheckboxCell from "./SelectionCheckboxCell";
import { useFrozenValue } from "../hooks/useFrozenValue";
import { useLookupColorMaps } from "../hooks/useLookups";
import { useSelectionSet } from "../hooks/useSelectionSet";
import { formatFieldValue } from "../utils/formatFieldValue";

export default function ImportPreviewDialog({
  open,
  items,
  onConfirm,
  onCancel,
  importing,
}) {
  const displayedItems = useFrozenValue(items, items.length > 0);
  const colorMaps = useLookupColorMaps();
  const itemIndexes = displayedItems.map((_, index) => index);
  const { selected, setSelected, allSelected, someSelected, toggleAll, toggleOne } =
    useSelectionSet(itemIndexes);

  useEffect(() => {
    setSelected(new Set(itemIndexes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedItems]);

  const handleConfirm = () => {
    onConfirm(displayedItems.filter((_, index) => selected.has(index)));
  };

  const styles = {
    title: { fontWeight: 700, color: "background.text" },
    background: { bgcolor: "background.dialog", color: "background.text" },
    summary: { mb: 2 },
    tableContainer: { maxHeight: 400, bgcolor: "background.tableHeader" },
    headerRow: {
      "& th": {
        fontWeight: 700,
        bgcolor: "background.tableHeader",
        color: "background.tableHeaderText",
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
      },
    },
    table: { minWidth: 1500 },
    columnWidths: {
      title: {
        fontWeight: 600,
        minWidth: 220,
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
      product: { minWidth: 160, whiteSpace: "normal", wordBreak: "break-word" },
      owner: { minWidth: 140, whiteSpace: "normal", wordBreak: "break-word" },
      description: {
        minWidth: 320,
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
      impactAreas: {
        minWidth: 180,
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
      links: { minWidth: 220, whiteSpace: "normal", wordBreak: "break-word" },
    },
    tableBody: { bgcolor: "background.tableBody" },
    unselectedRow: { opacity: 0.5 },
    actions: { px: 3, py: 2 },
    button: { textTransform: "none" },
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="lg"
      fullWidth
      slotProps={{ paper: { sx: styles.background } }}
    >
      <DialogTitle sx={styles.title}>Review Import</DialogTitle>
      <DialogContent>
        <Alert severity="info" variant="outlined" sx={styles.summary}>
          {selected.size} of {displayedItems.length} initiative
          {displayedItems.length !== 1 ? "s" : ""} selected.
        </Alert>
        <TableContainer sx={styles.tableContainer}>
          <Table size="small" stickyHeader sx={styles.table}>
            <TableHead>
              <TableRow sx={styles.headerRow}>
                <SelectionCheckboxCell
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                />
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Horizon</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Target Period</TableCell>
                <TableCell>Estimate</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Impact Areas</TableCell>
                <TableCell>GitLab Epic</TableCell>
                <TableCell>Design Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={styles.tableBody}>
              {displayedItems.map((item, index) => (
                <TableRow
                  key={index}
                  sx={!selected.has(index) ? styles.unselectedRow : null}
                >
                  <SelectionCheckboxCell
                    checked={selected.has(index)}
                    onChange={() => toggleOne(index)}
                  />
                  <TableCell sx={styles.columnWidths.title}>
                    {item.title}
                  </TableCell>
                  <TableCell sx={styles.columnWidths.description}>
                    {formatFieldValue(item.description)}
                  </TableCell>
                  <TableCell sx={styles.columnWidths.product}>
                    {formatFieldValue(item.product)}
                  </TableCell>
                  <TableCell sx={styles.columnWidths.owner}>
                    {formatFieldValue(item.owner)}
                  </TableCell>
                  <LookupChipCells item={item} colorMaps={colorMaps} />
                  <TableCell>{formatFieldValue(item.targetPeriod)}</TableCell>
                  <TableCell>
                    {item.estimate}{" "}
                    {item.estimate === 1 ? "quarter" : "quarters"}
                  </TableCell>
                  <TableCell>{item.progress}%</TableCell>
                  <TableCell sx={styles.columnWidths.impactAreas}>
                    {formatFieldValue(item.impactAreas)}
                  </TableCell>
                  <TableCell sx={styles.columnWidths.links}>
                    {formatFieldValue(item.gitlabEpic)}
                  </TableCell>
                  <TableCell sx={styles.columnWidths.links}>
                    {formatFieldValue(item.designLink)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button onClick={onCancel} disabled={importing} sx={styles.button}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={importing || selected.size === 0}
          sx={styles.button}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
